import React, { useCallback, useMemo, useEffect } from "react";
import { Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { FixedSizeList as List } from "react-window";
import ThreadListItem from "./threadListItem";
import ThreadListItemSkeleton from "./threadListItemSkeleton";
import {
    RuleBasedThread,
} from "../../../types/ruleBasedInbox";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import { useShallow } from "zustand/react/shallow";
import { RECENT_CHAT_CHECK_INTERVAL_MS } from "../constants";

const THREAD_ITEM_HEIGHT = 95;

interface ThreadListBodyProps {
    isLoading: boolean;
    onThreadSelect: (thread: RuleBasedThread) => void;
    listHeight: number;
}

/**
 * Virtualized thread list body.
 * Subscribes only to list/unread/recent state so search typing does not re-render rows.
 */
const ThreadListBody: React.FC<ThreadListBodyProps> = ({
    isLoading,
    onThreadSelect,
    listHeight
}) => {

    const {
        isThreadUnread,
        unreadThreads,
        isThreadRecent,
        recentThreadIds,
        refreshRecentThreads,
        initializeRecentThreads,
        clearAllRecentThreads,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
        threads,
        stats,
        selectedThreadId,
        getInstaAccountNameAndIdByIntegratedChannelId,
    } = useRuleBasedInboxStore(useShallow((state) => ({
        isThreadUnread: state.isThreadUnread,
        unreadThreads: state.unreadThreads,
        isThreadRecent: state.isThreadRecent,
        recentThreadIds: state.recentThreadIds,
        refreshRecentThreads: state.refreshRecentThreads,
        initializeRecentThreads: state.initializeRecentThreads,
        clearAllRecentThreads: state.clearAllRecentThreads,
        getWabaNameAndPhoneNumberBytIntegratedChannelId: state.getWabaNameAndPhoneNumberBytIntegratedChannelId,
        threads: state.threads,
        stats: state.stats,
        selectedThreadId: state.selectedThreadId,
        getInstaAccountNameAndIdByIntegratedChannelId: state.getInstaAccountNameAndIdByIntegratedChannelId
    })));

    // Check if we're on page 1
    const isOnPage1 = useMemo(() => {
        const currentStartFrom = typeof stats.start_from === "string"
            ? parseInt(stats.start_from, 10)
            : stats.start_from;
        return currentStartFrom === 1;
    }, [stats.start_from]);

    // Initialize recent threads when threads load on page 1
    useEffect(() => {
        if (!isLoading && threads.length > 0 && isOnPage1) {
            initializeRecentThreads();
        }
    }, [isLoading, threads.length, isOnPage1, initializeRecentThreads]);

    // Clear recent threads when navigating away from page 1
    useEffect(() => {
        if (!isOnPage1) {
            clearAllRecentThreads();
        }
    }, [isOnPage1, clearAllRecentThreads]);

    // Set up interval to check and refresh recent thread status every minute
    useEffect(() => {
        if (!isOnPage1 || isLoading) {
            return;  // Only run on page 1 when not loading
        }

        // Run the refresh check every RECENT_CHAT_CHECK_INTERVAL_MS
        const intervalId = setInterval(() => {
            refreshRecentThreads();
        }, RECENT_CHAT_CHECK_INTERVAL_MS);

        return () => {
            clearInterval(intervalId);
        };
    }, [isOnPage1, isLoading, refreshRecentThreads]);

    // Handle thread click
    const handleThreadClick = useCallback(
        (thread: RuleBasedThread) => {
            onThreadSelect(thread);
        },
        [onThreadSelect]
    );

    // Memoized item data for react-window
    const itemData = useMemo(
        () => ({
            threads,
            selectedThreadId,
            onThreadClick: handleThreadClick,
            isThreadUnread,
            unreadThreads, // Include to trigger re-render when unread status changes
            isThreadRecent,
            recentThreadIds, // Include to trigger re-render when recent status changes
            isOnPage1,
            getWabaNameAndPhoneNumberBytIntegratedChannelId,
            getInstaAccountNameAndIdByIntegratedChannelId,
        }),
        [
            threads,
            selectedThreadId,
            handleThreadClick,
            isThreadUnread,
            unreadThreads,
            isThreadRecent,
            recentThreadIds,
            isOnPage1,
            getWabaNameAndPhoneNumberBytIntegratedChannelId,
            getInstaAccountNameAndIdByIntegratedChannelId
        ]
    );

    // Render virtualized list item
    const renderListItem = useCallback(
        ({ index, style, data }: { index: number; style: React.CSSProperties; data: typeof itemData }) => {
            const thread = data.threads[index];
            const isSelected = data.selectedThreadId === thread.thread_id;

             // Check if thread has unread messages (only set by socket when chat not open)
            const hasUnreadMessages = data.isThreadUnread(thread.thread_id);

             // Check if thread is marked as recent (only on page 1)
            const isRecent = data.isOnPage1 && data.isThreadRecent(thread.thread_id);

            return (
                <ThreadListItem
                    key={thread.thread_id}
                    thread={thread}
                    isSelected={isSelected}
                    onThreadClick={data.onThreadClick}
                    style={style}
                    hasUnreadMessages={hasUnreadMessages}
                    isRecent={isRecent}
                    getWabaNameAndPhoneNumberBytIntegratedChannelId={data.getWabaNameAndPhoneNumberBytIntegratedChannelId}
                    getInstaAccountNameAndIdByIntegratedChannelId={data.getInstaAccountNameAndIdByIntegratedChannelId}
                />
            );
        },
        []
    );

    const renderEmptyState = () => (
        <div className={styles.emptyState}>
            <ChatBubbleOutlineIcon
                className={`${styles.emptyStateIcon} ${styles.emptyStateIconSize}`}
            />
            <Typography className={styles.emptyStateTitle}>
                No conversations here
            </Typography>
        </div>
    );

    const renderSkeletonItems = () => {
        const skeletonCount = Math.floor(listHeight / THREAD_ITEM_HEIGHT) || 5;
        return (
            <List
                height={listHeight}
                itemCount={skeletonCount}
                itemSize={THREAD_ITEM_HEIGHT}
                width="100%"
                className={styles.virtualizedList}
            >
                {({ index, style }) => (
                    <ThreadListItemSkeleton key={index} style={style} />
                )}
            </List>
        );
    };

    return (
        <div
            style={{ minHeight: listHeight }}
            className={styles.threadListContent}
        >
            {isLoading ? (
                renderSkeletonItems()
            ) : threads.length === 0 ? (
                renderEmptyState()
            ) : (
                <List
                    height={listHeight}
                    itemCount={threads.length}
                    itemSize={THREAD_ITEM_HEIGHT}
                    itemData={itemData}
                    width="100%"
                    className={styles.virtualizedList}
                >
                    {renderListItem}
                </List>
            )}
        </div>
    );
};

export default ThreadListBody;
